import type { Indicator } from '@protector/utils/types';
import { z } from 'zod';

export type TrapMeasurementStatus = 'DAMAGE' | 'CONTROL' | 'ACCEPTANCE' | 'NONE';

export interface TrapWindowIndicator {
  id: string;
  name: string;
  value: number;
  unit: string;
  severity?: TrapMeasurementStatus | null;
  liss?: number | null;
  decimal_places: number;
  is_smdl?: boolean;
}

export interface TrapWindowPhenomenon {
  id: string;
  name: string;
  indicators: TrapWindowIndicator[];
}

export interface TrapFieldResult {
  station_template_id: string;
  phenomena: TrapWindowPhenomenon[];
}

export interface TrapStationTemplate {
  id: string;
  name: string;
  description: string;
  icon_type: string;
  template_type: string;
  sampling_behavior: string;
  single_use: boolean;
  time_for_renewal: number;
}

export interface TrapPointGeometry {
  type: 'Point';
  coordinates: [number, number];
}

export interface TrapWindowPoint {
  id: string;
  station_id: string;
  point: TrapPointGeometry;
  forced_point: TrapPointGeometry | null;
  date: string;
  image: string | null;
  scouter: {
    id: string;
    name: string;
  };
  phenomena: TrapWindowPhenomenon[];
}

export interface TrapStation {
  id: string;
  name?: string | null;
  description: string;
  station_type: string;
  point: TrapPointGeometry;
  station_template_id: string;
  installed_field: string;
  field_ids?: string[] | null;
  single_use: boolean;
  sampling_behavior: string;
  installation_date: string;
  uninstallation_date: string | null;
  expiration_date: string | null;
  last_renewal: string | null;
  last_event_type: string;
  last_event_date: string;
  validity_in_days: number;
  validity_start_date: string;
}

export interface TrapWindowResponse {
  start_date: string;
  end_date: string;
  field_results: TrapFieldResult[];
  points: TrapWindowPoint[];
  stations: TrapStation[];
  station_templates: TrapStationTemplate[];
}

export interface TrapInfoVisual {
  trapTypeIcon: string;
}

export interface TrapInfoGroup {
  id: string;
  stationTemplateId: string;
  phenomenonId: string;
  trapType: string;
  speciesName: string;
  trapTypeIcon: string;
  evaluatedTrapCount: number;
  measurements: Indicator[];
}

const trapPointGeometrySchema = z.object({
  type: z.literal('Point'),
  coordinates: z.tuple([z.number(), z.number()])
});

const trapWindowIndicatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  value: z.number(),
  unit: z.string(),
  severity: z.enum(['DAMAGE', 'CONTROL', 'ACCEPTANCE', 'NONE']).nullish(),
  liss: z.number().nullable().optional(),
  decimal_places: z.number(),
  is_smdl: z.boolean().optional()
});

const trapWindowPhenomenonSchema = z.object({
  id: z.string(),
  name: z.string(),
  indicators: z.array(trapWindowIndicatorSchema)
});

export const trapWindowResponseSchema = z.object({
  start_date: z.string(),
  end_date: z.string(),
  field_results: z.array(
    z.object({
      station_template_id: z.string(),
      phenomena: z.array(trapWindowPhenomenonSchema)
    })
  ),
  points: z.array(
    z.object({
      id: z.string(),
      station_id: z.string(),
      point: trapPointGeometrySchema,
      forced_point: trapPointGeometrySchema.nullable(),
      date: z.string(),
      image: z.string().nullable(),
      scouter: z.object({ id: z.string(), name: z.string() }),
      phenomena: z.array(trapWindowPhenomenonSchema)
    })
  ),
  stations: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullish(),
      description: z.string(),
      station_type: z.string(),
      point: trapPointGeometrySchema,
      station_template_id: z.string(),
      installed_field: z.string(),
      field_ids: z.array(z.string()).nullable().optional(),
      single_use: z.boolean(),
      sampling_behavior: z.string(),
      installation_date: z.string(),
      uninstallation_date: z.string().nullable(),
      expiration_date: z.string().nullable(),
      last_renewal: z.string().nullable(),
      last_event_type: z.string(),
      last_event_date: z.string(),
      validity_in_days: z.number(),
      validity_start_date: z.string()
    })
  ),
  station_templates: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      icon_type: z.string(),
      template_type: z.string(),
      sampling_behavior: z.string(),
      single_use: z.boolean(),
      time_for_renewal: z.number()
    })
  )
});
